import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { OrgAdminAssignmentDto } from 'types/api.types';
import { getOrganizations } from 'services/lookupsService';
import {
  assignOrgAdmin,
  getOrgAdminAssignments,
  removeOrgAdminAssignment,
  updateOrgAdminAssignment,
} from 'services/orgAdminsService';

type ToastSeverity = 'success' | 'error';

const OrgAdmins: React.FC = () => {
  const [assignments, setAssignments] = useState<OrgAdminAssignmentDto[]>([]);
  const [organizations, setOrganizations] = useState<{ id: number; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<number | ''>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdatingByKey, setIsUpdatingByKey] = useState<Record<string, boolean>>({});
  const [isRemovingByKey, setIsRemovingByKey] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; severity: ToastSeverity } | null>(null);

  const showErrorToast = (message: string) => setToast({ message, severity: 'error' });
  const showSuccessToast = (message: string) => setToast({ message, severity: 'success' });

  const assignmentKey = (row: OrgAdminAssignmentDto) => `${row.adminEmail}:${row.orgId}`;

  const sortedAssignments = useMemo(
    () =>
      [...assignments].sort((a, b) => {
        const emailCompare = a.adminEmail.localeCompare(b.adminEmail);
        if (emailCompare !== 0) return emailCompare;
        return a.orgName.localeCompare(b.orgName);
      }),
    [assignments],
  );

  const loadData = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    const [assignmentResult, orgs] = await Promise.all([
      getOrgAdminAssignments(),
      getOrganizations(),
    ]);

    if (!assignmentResult || !orgs) {
      setErrorMessage('Unable to load org admin assignments or organizations.');
      setIsLoading(false);
      return;
    }

    setAssignments(assignmentResult.assignments);
    setOrganizations(orgs.map((org) => ({ id: org.id, name: org.name })));
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAssign = async () => {
    const email = adminEmail.trim().toLowerCase();
    if (!email) {
      showErrorToast('Admin email is required.');
      return;
    }
    if (typeof selectedOrgId !== 'number') {
      showErrorToast('Select an organization.');
      return;
    }

    setIsAssigning(true);
    const created = await assignOrgAdmin(email, selectedOrgId);
    setIsAssigning(false);

    if (!created) {
      showErrorToast('Unable to assign organization to admin.');
      return;
    }

    setAssignments((current) => {
      const key = assignmentKey(created);
      const withoutExisting = current.filter((row) => assignmentKey(row) !== key);
      return [...withoutExisting, created];
    });
    setAdminEmail('');
    setSelectedOrgId('');
    showSuccessToast('Organization assigned to admin.');
  };

  const handleToggleActive = async (row: OrgAdminAssignmentDto) => {
    const key = assignmentKey(row);
    setIsUpdatingByKey((current) => ({ ...current, [key]: true }));
    const updated = await updateOrgAdminAssignment(row.adminEmail, row.orgId, !row.isActive);
    setIsUpdatingByKey((current) => ({ ...current, [key]: false }));

    if (!updated) {
      showErrorToast('Unable to update assignment status.');
      return;
    }

    setAssignments((current) =>
      current.map((assignment) => (assignmentKey(assignment) === key ? updated : assignment)),
    );
    showSuccessToast(updated.isActive ? 'Assignment activated.' : 'Assignment deactivated.');
  };

  const handleRemove = async (row: OrgAdminAssignmentDto) => {
    const key = assignmentKey(row);
    setIsRemovingByKey((current) => ({ ...current, [key]: true }));
    const result = await removeOrgAdminAssignment(row.adminEmail, row.orgId);
    setIsRemovingByKey((current) => ({ ...current, [key]: false }));

    if (!result.success) {
      showErrorToast(result.errorMessage ?? 'Unable to remove assignment.');
      return;
    }

    setAssignments((current) => current.filter((assignment) => assignmentKey(assignment) !== key));
    showSuccessToast('Assignment removed.');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        Org admin management
      </Typography>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Assign organization to admin
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            label="Admin email"
            value={adminEmail}
            onChange={(event) => setAdminEmail(event.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="org-admin-org-select-label">Organization</InputLabel>
            <Select
              labelId="org-admin-org-select-label"
              label="Organization"
              value={selectedOrgId}
              onChange={(event) => setSelectedOrgId(event.target.value as number)}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={isAssigning}
            sx={{ minWidth: 140 }}
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current assignments
        </Typography>
        {sortedAssignments.length === 0 ? (
          <Typography color="text.secondary">No org admin assignments yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Admin email</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAssignments.map((row) => {
                const key = assignmentKey(row);
                return (
                  <TableRow key={key}>
                    <TableCell>{row.adminEmail}</TableCell>
                    <TableCell>{row.orgName}</TableCell>
                    <TableCell>
                      <Switch
                        checked={row.isActive}
                        onChange={() => handleToggleActive(row)}
                        disabled={isUpdatingByKey[key]}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleRemove(row)}
                        disabled={isRemovingByKey[key]}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
};

export default OrgAdmins;
